import { createSlice } from "@reduxjs/toolkit";

// Workspace-wide "what's happening right now" view — deliberately separate
// from callSlice.js, which only ever tracks the one call *this* agent's tab
// is ringing/connected on. This slice can hold many entries at once (every
// agent's live call, seen by everyone), keyed by callId for O(1) upsert/removal
// as socket events arrive — never re-fetched, just patched in place.
const initialState = {
  byId: {},
  hydrated: false, // true once the initial GET /api/calling/live snapshot has landed
};

const liveCallsSlice = createSlice({
  name: "liveCalls",
  initialState,
  reducers: {
    liveCallsHydrated(state, action) {
      const byId = {};
      for (const call of action.payload || []) {
        byId[call.callId] = call;
      }
      state.byId = byId;
      state.hydrated = true;
    },
    // A call just started ringing (inbound or outbound) — call:live_update.
    liveCallUpserted(state, action) {
      const call = action.payload;
      if (!call?.callId) return;
      state.byId[call.callId] = { ...state.byId[call.callId], ...call };
    },
    // call:claimed — an agent answered, failed to answer, or declined.
    // outcome distinguishes the three so this resolves straight to its final
    // display state instead of a "connected" flash that a later event corrects.
    liveCallClaimed(state, action) {
      const { callId, claimedBy, agentName, outcome } = action.payload || {};
      if (!callId || !state.byId[callId]) return;
      if (outcome === "connected") {
        state.byId[callId].status = "connected";
        state.byId[callId].agentId = claimedBy || null;
        state.byId[callId].agentName = agentName || null;
        state.byId[callId].connectedAt = Date.now();
      } else {
        delete state.byId[callId];
      }
    },
    // call:answered — outbound only: the customer picked up.
    liveCallAnswered(state, action) {
      const { callId } = action.payload || {};
      if (!callId || !state.byId[callId]) return;
      state.byId[callId].status = "connected";
      state.byId[callId].connectedAt = Date.now();
    },
    // call:ended — terminal for every direction/outcome; no longer "live".
    liveCallEnded(state, action) {
      const { callId } = action.payload || {};
      if (callId) delete state.byId[callId];
    },
    // call:transferCompleted — the call's owning agent changed; everyone's
    // sidebar (not just the two agents involved) needs the new name to show.
    liveCallTransferred(state, action) {
      const { callId, newAgentId, newAgentName } = action.payload || {};
      if (!callId || !state.byId[callId]) return;
      state.byId[callId].agentId = newAgentId || null;
      state.byId[callId].agentName = newAgentName || null;
    },
  },
});

export const {
  liveCallsHydrated,
  liveCallUpserted,
  liveCallClaimed,
  liveCallAnswered,
  liveCallEnded,
  liveCallTransferred,
} = liveCallsSlice.actions;

export default liveCallsSlice.reducer;
